// src/features/leases/components/LeaseSummaryCard.jsx
//
// LSE-TAB-REDESIGN Part 2 — new card chrome for the Lease Summary panel.
// Renders 7 fields: Type, Areas (conditional), Provider (conditional),
// Lessor, Lessee, Commencement, End date, Break clause (conditional),
// Term length, Lease ID.
//
// LSE-UNIFIED-EDIT-UX-PHASE-2 Sub-phase 1d: all inline-edit affordances
// removed. canWrite / onSaved props dropped. Edits go through EditLeaseModal.
// EditableLeaseField retained as a pure read-only display component
// (canWrite always false).
//
// TASK-2707: Break clause (breakDate) is now shown inline after End date.
// Mutual breaks include the notice deadline when breakNoticeMonths is set.
//
// LEASE-TERMS-REDESIGN: Key terms variant gains:
//   - Count pill ("N of M") in the section header.
//   - "Not in this lease: …  + add" footer for absent canonical fields.
//   - Year-based countdown chip on End date ("~N yrs" not "Xd").
//   - Term length formatted as "N mo · ~N yrs".
//   canWrite prop added to show "+ add" for editors/admins.

import React, { useState, useCallback, useRef, useEffect } from "react";
import { ClampedProse } from "../../../components/ClampedProse.jsx";
import { EditableLeaseField } from "./EditableLeaseField.jsx";
import { DaysRemainingPill } from "./DaysRemainingPill.jsx";
import { LEASE_TYPE_OPTIONS, leaseTypeLabel } from "../leaseTypes.js";
import { sourceFor } from "../LeaseSummaryTab.jsx";
import { isSentinelEndDate, parseDateOnly, KEY_TERMS_FIELDS } from "../leaseTabConfig.js";
import { T } from "../../../app/tokens.js";
import { formatFootprintSqftFull as formatSqftFull } from "../../../utils/format.js";
import { currentLeaseFootprint } from "../../../../shared/leaseAreaBasis.js";
import { PaneHeading } from "../../../components/PaneHeading.jsx";
import { generateLeaseSummary } from "../../../data/leasesClient.js";



function fmtDate(d) {
  if (isSentinelEndDate(d)) return "Ongoing";
  const dt = parseDateOnly(d);
  if (!dt) return d == null ? null : String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Month-year only (e.g. "Feb 2028") — used for break clause and notice deadline
// where the day is not meaningful for the portfolio manager's decision window.
function fmtDateShort(d) {
  if (!d) return null;
  if (isSentinelEndDate(d)) return "Ongoing";
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short" });
}

function daysFrom(today, dateStr) {
  if (isSentinelEndDate(dateStr)) return null;
  const dt = parseDateOnly(dateStr);
  if (!dt) return null;
  return Math.ceil((dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// "~N yrs" chip for end-date remaining — shows years instead of days
// to match the brief ("consistent with Offices").
function YearsRemainingChip({ days }) {
  if (days == null || days <= 0) return null;
  const yrs = Math.round(days / 365);
  return (
    <span
      data-testid="years-remaining-chip"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        padding:      "2px 10px",
        borderRadius: 6,
        background:   T.pillGreenBg,
        color:        T.forestGreen,
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        lineHeight:   1.2,
      }}
    >
      ~{yrs} yrs
    </span>
  );
}

// Determines whether a canonical KEY_TERMS_FIELDS entry is "specified"
// (has a non-null, non-empty value) given the lease + computed objects.
function isKeyTermSpecified(fieldDef, lease, computed) {
  if (fieldDef.field === "termLengthMonths") {
    return computed?.termLengthMonths != null || isSentinelEndDate(lease.endDate);
  }
  const v = lease[fieldDef.field];
  return v != null && v !== "";
}

const CARD_STYLE = {
  background: T.card,
  borderRadius: 6,
  padding: "11px 13px",
};

// ─── LessorContactsExpander ───────────────────────────────────────────────────
// Collapsible panel for lessor contact entries in the Parties variant.
// Shows "Lessor contacts (N) ▾" toggle; expands to show all contact rows.
function LessorContactsExpander({ contacts }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="lessor-contacts-block" style={{ marginTop: 4 }}>
      <button
        type="button"
        data-testid="lessor-contacts-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          background:     "none",
          border:         "none",
          padding:        "4px 0",
          cursor:         "pointer",
          display:        "flex",
          alignItems:     "center",
          gap:            4,
          fontSize:       T.fsXs,
          color:          T.blue,
          fontWeight:     T.fwSemibold,
        }}
      >
        Lessor contacts ({contacts.length}) {open ? "▴" : "▾"}
      </button>
      {open && (
        <div style={{ marginTop: 4 }}>
          {contacts.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <div style={{ borderTop: `1px solid ${T.border}`, margin: "8px 0" }} />
              )}
              <div
                data-testid={`lessor-contact-entry-${i}`}
                style={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                {c.contactType && (
                  <div style={{ fontSize: T.fsXxs, color: T.light }}>{c.contactType}</div>
                )}
                {c.name && (
                  <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
                    {c.name}
                  </div>
                )}
                {c.address && (
                  <div style={{ fontSize: T.fsXs, color: T.mid }}>{c.address}</div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

const dateRender = (v) => (
  <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{fmtDate(v)}</span>
);

// LEASE-DETAIL-TABS-SHELL: variant="parties" renders a compact party-fields
// summary (Lessor, Lessee, Lessor contact) for the Overview tab.
// variant="summary" renders the AI lease summary card for the Overview tab.
// The default (no variant) renders all key-terms fields for the Terms tab.
// Statuses that count as "active" for the summary pane sub-count.
const ACTIVE_STATUSES = new Set(["active", "holdover", "in_negotiation"]);

export function LeaseSummaryCard({ lease, computed, today = new Date(), variant, canWrite = false, leases, onGenerated, onRefetch, onEditOpen, onGeneratingChange, measuredProse = false, contentOnly = false }) {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [generating,      setGenerating]      = useState(false);
  const [generationError, setGenerationError] = useState(null);
  // Task #9096: optional in-flight signal for drawer navigation guards.
  const generatingChangeRef = useRef(onGeneratingChange);
  generatingChangeRef.current = onGeneratingChange;
  useEffect(() => { generatingChangeRef.current?.(generating); }, [generating]);

  const handleGenerate = useCallback(async () => {
    if (!lease?.locationId || !lease?.id) return;
    setGenerating(true);
    setGenerationError(null);
    let generated = false;
    try {
      const result = await generateLeaseSummary(lease.locationId, lease.id);
      generated = true;
      if (onGenerated) await onGenerated(lease.id, result);
      if (onRefetch) await onRefetch();
    } catch (_err) {
      setGenerationError(generated
        ? "Summary was generated, but the view could not refresh. Reload to see the saved summary."
        : "Summary generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  }, [lease, onGenerated, onRefetch]);

  const base = {
    locationId: lease.locationId,
    leaseId: lease.id,
    lease,
    canWrite: false,
  };

  if (variant === "summary") {
    const summary = lease.aiSummary || null;
    const activeCount = Array.isArray(leases)
      ? leases.filter((l) => ACTIVE_STATUSES.has(l.status)).length
      : null;

    // Overflow detection: only show expand button when the prose actually overflows
    // its container (scrollHeight > clientHeight). ResizeObserver re-checks on resize.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const prosRef = useRef(null);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [isOverflowing, setIsOverflowing] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const el = prosRef.current;
      if (!el) return;
      const check = () => setIsOverflowing(el.scrollHeight > el.clientHeight);
      check();
      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
      }
    }, [summary, summaryExpanded]);

    return (
      <section
        data-testid="lease-summary-card-ai-summary"
        style={{
          ...CARD_STYLE,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: summaryExpanded ? "auto" : "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <PaneHeading
            sub={activeCount != null ? `${activeCount} active` : undefined}
            style={{ flex: 1, marginBottom: 0 }}
          >
            Lease summary
          </PaneHeading>
          {canWrite && (
            <button
              type="button"
              data-testid="generate-summary-btn"
              disabled={generating}
              onClick={handleGenerate}
              style={{
                background:   "none",
                border:       `1px solid ${T.blue}`,
                borderRadius: 4,
                padding:      "2px 10px",
                fontSize:     T.fsXxs,
                fontWeight:   T.fwSemibold,
                color:        T.blue,
                cursor:       generating ? "not-allowed" : "pointer",
                opacity:      generating ? 0.6 : 1,
                whiteSpace:   "nowrap",
                flexShrink:   0,
                marginLeft:   8,
              }}
            >
              {generating ? "Generating…" : summary ? "↻ Regenerate" : "Generate summary"}
            </button>
          )}
        </div>
        {generationError && (
          <div
            data-testid="generate-summary-error"
            role="alert"
            style={{
              fontSize: T.fsXs,
              color: T.errorText,
              background: T.redSoft,
              borderRadius: 4,
              padding: "6px 10px",
              marginBottom: 10,
            }}
          >
            {generationError}
          </div>
        )}
        {summary && measuredProse ? (
          <ClampedProse text={summary.trim()} lines={3} resetKey={lease.id} textId={`lease-summary-text-${lease.id}`}
            textStyle={{ margin: 0, fontSize: T.fsBase, color: T.dark, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            testId="ai-summary-text" probeTestId="ai-summary-probe" toggleTestId="ai-summary-toggle" />
        ) : summary ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {(() => {
              const rawParagraphs = summary.trim().split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
              const paragraphs = rawParagraphs.length > 1
                ? rawParagraphs
                : summary.trim().split(/\n/).map((p) => p.trim()).filter(Boolean);
              return (
                <div
                  ref={prosRef}
                  data-testid="ai-summary-text"
                  style={{
                    fontSize: T.fsBase,
                    color: T.mid,
                    lineHeight: 1.6,
                    ...(summaryExpanded
                      ? { overflow: "visible" }
                      : { flex: 1, overflow: "hidden" }),
                  }}
                >
                  {paragraphs.map((para, i) => (
                    <p key={i} style={{ margin: 0, marginTop: i === 0 ? 0 : "0.75em" }}>{para}</p>
                  ))}
                </div>
              );
            })()}
            {(isOverflowing || summaryExpanded) && (
              <button
                type="button"
                data-testid="ai-summary-expand-btn"
                onClick={() => setSummaryExpanded((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginTop: 6,
                  cursor: "pointer",
                  color: T.blue,
                  fontSize: T.fsBase,
                  fontWeight: T.fwSemibold,
                  textDecoration: "underline",
                  flexShrink: 0,
                }}
              >
                {summaryExpanded ? "Show less" : "Read full summary"}
              </button>
            )}
          </div>
        ) : (
          <p
            data-testid="ai-summary-empty"
            style={{ fontSize: T.fsXs, color: T.light, fontStyle: "italic", margin: 0 }}
          >
            No lease summary on record yet.
          </p>
        )}
      </section>
    );
  }

  if (variant === "parties") {
    const lessorContactRaw = lease.lessorContact ? String(lease.lessorContact) : null;

    // Split by newline first so each line is a discrete contact entry.
    // Expected line format: "{contact type} · {name} · {address}"
    const contactEntries = lessorContactRaw
      ? lessorContactRaw.split("\n").map((line) => line.trim()).filter(Boolean)
      : [];

    // Parse each entry into labeled parts by middle-dot delimiter.
    const contacts = contactEntries.map((entry) => {
      const parts = entry.split(" · ").map((p) => p.trim());
      return {
        contactType: parts[0] || null,
        name:        parts[1] || null,
        address:     parts.length > 2 ? parts.slice(2).join(" · ") : null,
      };
    });

    return (
      <section data-testid="lease-summary-card-parties" style={contentOnly ? {} : { ...CARD_STYLE, height: "100%", boxSizing: "border-box" }}>
        {!contentOnly && <PaneHeading style={{ marginBottom: 10 }}>Parties</PaneHeading>}
        <EditableLeaseField {...base}
          field="vendor" label="Lessor" value={lease.vendor}
          source={sourceFor(lease, "vendor")} />
        <EditableLeaseField {...base}
          field="lessee" label="Lessee" value={lease.lessee}
          source={sourceFor(lease, "lessee")} />
        {contacts.length > 0 && (
          <LessorContactsExpander contacts={contacts} />
        )}
      </section>
    );
  }

  // ── Key terms (default variant) ──────────────────────────────────────────

  // Compute count pill: specified vs total for canonical KEY_TERMS_FIELDS.
  const specifiedCount = KEY_TERMS_FIELDS.filter((f) => isKeyTermSpecified(f, lease, computed)).length;
  const totalCount = KEY_TERMS_FIELDS.length;
  const pillFull = specifiedCount === totalCount;

  // Unspecified canonical fields — shown in "Not in this lease" footer.
  // Always-present fields (type, startDate, endDate, termLengthMonths, id)
  // will pass the isKeyTermSpecified check so they never appear here.
  const unspecifiedKeyTerms = KEY_TERMS_FIELDS
    .filter((f) => !isKeyTermSpecified(f, lease, computed));
  const unspecifiedLabels = unspecifiedKeyTerms.map((f) => f.label);
  // First absent field key — passed to onEditOpen so the modal can pre-focus it.
  const firstAbsentField = unspecifiedKeyTerms[0]?.field ?? null;

  // Days remaining to the end date for year chip.
  const daysToExpiry = computed?.daysToExpiry ?? daysFrom(today, lease.endDate);

  return (
    <section data-testid="lease-summary-card" style={{ ...CARD_STYLE, border: `1px solid ${T.border}` }}>
      {/* Header with count pill */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 3, height: 14, borderRadius: 2, background: T.blue, flexShrink: 0 }} />
        <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, flex: 1 }}>Key terms</span>
        <span
          data-testid="key-terms-count-pill"
          style={{
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
            color:        pillFull ? T.forestGreen : T.light,
            background:   pillFull ? T.pillGreenBg : T.tint,
            borderRadius: 999,
            padding:      "2px 9px",
          }}
        >
          {specifiedCount} of {totalCount}
        </span>
      </div>

      <EditableLeaseField {...base}
        field="type" label="Type" value={lease.type}
        type="select" options={LEASE_TYPE_OPTIONS}
        render={(v) => <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{leaseTypeLabel(v)}</span>}
        source={sourceFor(lease, "type")} />
      {/* LEASE-NEW-FIELDS-DISPLAY: termType promoted from accordion to summary section */}
      {lease.termType && (
        <EditableLeaseField {...base}
          field="termType" label="Term type" value={lease.termType}
          source={sourceFor(lease, "termType")} />
      )}
      {typeof lease.areas === "string" && lease.areas && (
        <EditableLeaseField {...base}
          field="areas" label="Areas" value={lease.areas}
          render={(v) => <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{v}</span>}
          source={sourceFor(lease, "areas")} />
      )}
      {lease.type === "serviced_office" && lease.servicedOfficeProvider && (
        <EditableLeaseField {...base}
          field="servicedOfficeProvider" label="Provider" value={lease.servicedOfficeProvider}
          source={sourceFor(lease, "servicedOfficeProvider")} />
      )}
      <EditableLeaseField {...base}
        field="size" label="Size" value={currentLeaseFootprint(lease).grossSf}
        render={(value) => <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
          {value == null ? "—" : formatSqftFull(Number(value))}
        </span>}
        source={currentLeaseFootprint(lease).source === "legacy_lease_size" ? sourceFor(lease, "size") : undefined}
      />
      <EditableLeaseField {...base}
        field="startDate" label="Commencement" value={lease.startDate}
        type="date" render={dateRender}
        source={sourceFor(lease, "startDate")} />
      {/* LEASE-FIELDS-ADD-DROP: possessionDate from Spacebase (migration 0216) */}
      {lease.possessionDate && (
        <EditableLeaseField {...base}
          field="possessionDate" label="Possession" value={lease.possessionDate}
          type="date" render={dateRender}
          source={sourceFor(lease, "possessionDate")} />
      )}
      <EditableLeaseField {...base}
        field="endDate" label="End date" value={lease.endDate}
        type="date" render={dateRender}
        dateBadge={<YearsRemainingChip days={daysToExpiry} />}
        source={sourceFor(lease, "endDate")} />
      {/* Per LSE-PROVENANCE-BADGE-SEMANTICS (#53): termLengthMonths is always
          derived (endDate − startDate) — always a C (computed) badge unless
          fieldSources.termLengthMonths explicitly overrides it (e.g. "manual"). */}
      <EditableLeaseField {...base}
        field="termLengthMonths" label="Term length"
        value={computed?.termLengthMonths}
        render={(v) => {
          if (isSentinelEndDate(lease.endDate) || v == null) {
            return <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>Ongoing</span>;
          }
          const months = Number(v);
          const years = Math.round(months / 12);
          return (
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
              {months} mo · ~{years} yrs
            </span>
          );
        }}
        source={lease.fieldSources?.termLengthMonths || "computed"} />
      {/* Per LSE-PROVENANCE-BADGE-SEMANTICS (#53): Lease ID is a system-assigned
          identifier — no provenance badge unless fieldSources.id is explicitly set. */}
      <EditableLeaseField {...base}
        field="id" label="Lease ID" value={lease.id}
        source={lease.fieldSources?.id} />

      {/* "Not in this lease" footer for absent canonical fields */}
      {unspecifiedLabels.length > 0 && (
        <div
          data-testid="key-terms-not-in-lease"
          style={{
            marginTop:    9,
            fontSize:     T.fsXs,
            color:        T.light,
            fontWeight:   T.fwSemibold,
            background:   T.tint,
            border:       `1px dashed ${T.border}`,
            borderRadius: 8,
            padding:      "8px 11px",
          }}
        >
          <span>Not in this lease: {unspecifiedLabels.join(" · ")}</span>
          {canWrite && (
            <button
              type="button"
              data-testid="key-terms-add-btn"
              onClick={() => onEditOpen?.(firstAbsentField)}
              style={{
                display:    "block",
                marginTop:  6,
                background: "none",
                border:     "none",
                padding:    0,
                cursor:     "pointer",
                color:      T.blue,
                fontWeight: T.fwBold,
                fontSize:   T.fsXs,
              }}
            >
              + add
            </button>
          )}
        </div>
      )}
    </section>
  );
}
